import { useContext, useRef, useState } from 'react'
import { Box, Button, CircularProgress, Grid, Typography, useTheme } from '@mui/material'
import { Form, Formik } from 'formik'
import { useSnackbar } from 'notistack'
import * as Yup from 'yup'

import addPhotoImage from '../../images/add-photo.svg'
import { DashboardContext } from '../../providers/dashboard-provider'
import Api from '../../utils/api'
import { locationsListArray } from '../../utils/helpers'
import useFolderTheme from '../../utils/hooks/useFolderTheme'
import useLoadDevicePhoto from '../../utils/hooks/useLoadDevicePhoto'
import FormikDropdown from '../formik-dropdown'
import FormikTextField from '../formik-input'
import FullScreenModal from '../full-screen-modal'

import { autoCropImage, validateImageFile } from './helpers'
import useStyles from './index.styles'

interface Props {
  open: boolean
  onClose: () => void
  device: IDeviceType | null
  onSuccess?: () => void
}

const DeviceLocationModal = (props: Props) => {
  const { open, onClose, device, onSuccess } = props

  const classes = useStyles
  const theme = useTheme()

  const { enqueueSnackbar } = useSnackbar()
  const { setRefreshDevicesCounter } = useContext(DashboardContext)

  const folderId = device?.node?.folderId
  const { folderTheme } = useFolderTheme(folderId)

  const fileInputRef = useRef<HTMLInputElement>(null)
  const [hasPhoto, setHasPhoto] = useState<boolean>(device?.hasPhoto || false)
  const [isUploadingPhoto, setIsUploadingPhoto] = useState<boolean>(false)

  const shouldLoad = !!device?.hasPhoto
  const { photoPath, setPhotoPath } = useLoadDevicePhoto(device?.node?.id, shouldLoad, 'thumb')

  const locationsList = locationsListArray(folderTheme)
  const locations = locationsList.sort().map((deviceLocation: string) => ({ id: deviceLocation, name: deviceLocation }))

  const initialValues = {
    location: device?.node?.area || '',
    locationDetails: device?.deviceLocationNotes || '',
    building: device?.node?.building || '',
    floor: device?.node?.floor || '',
  }

  const validationSchema = Yup.object().shape({
    location: Yup.string().trim().required('Required'),
    floor: Yup.string().max(4, 'Floor must be four or fewer characters'),
    locationDetails: Yup.string().trim().required('Required'),
  })

  const handleSubmit = async (formValues: typeof initialValues) => {
    const payload = {
      deviceLocationNotes: formValues.locationDetails,
      building: formValues.building,
      floor: formValues.floor,
      area: formValues.location,
    }

    const res = await Api.patch(`/api/v2/protect/devices/${device?.node.id}`, payload)
    if (res.ok) {
      enqueueSnackbar(`Device updated successfully.`)
      if (onSuccess) {
        onSuccess()
      }
      setRefreshDevicesCounter((prev) => prev + 1)
      onClose()
    } else {
      enqueueSnackbar(`Could not update device.`, { variant: 'error' })
    }
  }

  const handlePhotoButtonClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file || !device) return

    const validation = validateImageFile(file)
    if (!validation.valid) {
      enqueueSnackbar(validation.error, { variant: 'error' })
      return
    }

    try {
      setIsUploadingPhoto(true)

      // Resize image if larger than 812x812
      const processedBlob = await autoCropImage(file, 812, 812)

      const formData = new FormData()
      formData.append('photo', processedBlob, 'photo.jpg')

      const response = await Api.put(`/api/v2/devices/${device.node.id}/photo`, formData)

      if (response.ok) {
        // Create object URL for thumbnail display
        const uploadedImageUrl = URL.createObjectURL(processedBlob)
        setPhotoPath(uploadedImageUrl)
        setHasPhoto(true)
        enqueueSnackbar('Device photo updated successfully.', { variant: 'success' })
      } else {
        enqueueSnackbar('Error uploading photo. Please try again.', { variant: 'error' })
      }
    } catch {
      enqueueSnackbar('Error uploading photo. Please try again.', { variant: 'error' })
    } finally {
      setIsUploadingPhoto(false)
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  if (!device) return null

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit} enableReinitialize={true} validationSchema={validationSchema}>
      {({ isSubmitting, submitForm }) => (
        <FullScreenModal
          open={open}
          onClose={onClose}
          showCloseIcon={true}
          title="Device Location"
          maxWidth="sm"
          confirmButton={
            <Button type="submit" variant="contained" onClick={submitForm} disableElevation disabled={isSubmitting}>
              Save
            </Button>
          }
        >
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept=".jpg,.jpeg,.png,.gif"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
              id="device-photo-upload"
              aria-label="Device photo upload"
            />

            {hasPhoto && photoPath ? (
              <>
                <img src={photoPath} alt="Photo of device" style={classes.thumbnail(theme)} />

                <Grid size={12} sx={classes.centeredGrid}>
                  <Button variant="text" onClick={handlePhotoButtonClick} sx={classes.changePhotoBtn} disabled={isUploadingPhoto}>
                    {isUploadingPhoto ? 'Uploading...' : 'Change Photo'}
                  </Button>
                </Grid>
              </>
            ) : (
              <>
                <img src={addPhotoImage} alt="Add Photo" width={80} height={80} style={classes.addPhoto(theme)} />

                <Grid size={12} sx={classes.centeredGrid}>
                  <Button variant="text" onClick={handlePhotoButtonClick} disabled={isUploadingPhoto}>
                    {isUploadingPhoto ? 'Uploading...' : 'Add Location Photo'}
                  </Button>

                  <Typography sx={classes.recommendedText(theme)}>Recommended</Typography>
                </Grid>
              </>
            )}

            <Typography variant="body1" color="text.secondary">
              Adding{' '}
              <Box component="span" sx={classes.detailedInfo}>
                detailed information
              </Box>{' '}
              below will help you easily locate your sensor during alerts.
            </Typography>

            <Form>
              <Grid size={12}>
                {!folderTheme ? (
                  <CircularProgress />
                ) : (
                  <FormikDropdown
                    name="location"
                    label="Select device location"
                    defaultValue={initialValues.location}
                    required={true}
                    variant="standard"
                    items={locations}
                    size="medium"
                    slotProps={{
                      select: {
                        MenuProps: {
                          style: {
                            height: '500px',
                          },
                        },
                      },
                    }}
                  />
                )}
              </Grid>

              <Grid size={12}>
                <FormikTextField name="locationDetails" label="Device location details*" fullWidth={true} multiline={true} rows={1} variant="standard" />
              </Grid>

              <Typography variant="body2" color="text.secondary" sx={classes.optionalText}>
                Optional
              </Typography>

              <Grid container={true} size={12} spacing={4}>
                <Grid size={{ xs: 8, sm: 9 }} sx={{ order: { xs: 2, sm: 1 } }}>
                  <FormikTextField name="building" label="Building" variant="standard" />
                </Grid>

                <Grid size={{ xs: 4, sm: 3 }} sx={{ order: { xs: 1, sm: 2 } }}>
                  <FormikTextField name="floor" label="Floor" variant="standard" />
                </Grid>
              </Grid>
            </Form>
          </>
        </FullScreenModal>
      )}
    </Formik>
  )
}

export default DeviceLocationModal
